import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import {
  type WorkflowStep,
  WorkflowStepStatuses,
  WorkflowStepUserResponses,
} from "~/api/types";
import { ArrowBigDown, ArrowBigUp, Play } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";
import { toast } from "react-toastify";
import { moveWorkflow } from "~/api/v2/requests";
import i18n from "~/i18n";
import { type DocumentDetailsVersion } from "~/api/types";
import {
  canManageVersion,
  canPublishVersion,
  getApprovalStatus,
  getWorkflowStepControlPermissions,
} from "~/helpers/documentPermissions";

type Props = {
  step: WorkflowStep;
  stepIndex: number;
  totalSteps: number;
  documentVersionId: number;
  selectedVersion: DocumentDetailsVersion;
  isWorkflowStarted: boolean;
  children: React.ReactNode;
  onChange: () => void;
};

const ConfirmMessage = {
  someRejected: {
    title: i18n.t(($) => $.commons.workflow.proceedWithRejectedResponses),
    description: i18n.t(($) => $.commons.workflow.someParticipantsRejected),
  },
  notAllResponded: {
    title: i18n.t(($) => $.commons.workflow.proceedWithoutAllResponses),
    description: i18n.t(($) => $.commons.workflow.notAllResponded),
  },
} as const;

export function WorkflowOverviewStepControlContainer({
  step,
  stepIndex,
  totalSteps,
  documentVersionId,
  selectedVersion,
  isWorkflowStarted,
  children,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canManage = canManageVersion(selectedVersion);
  const [showConfirmDialog, setShowConfirmDialog] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const handleTransition = async (direction: "backward" | "forward") => {
    try {
      setIsSubmitting(true);
      await moveWorkflow(documentVersionId, direction);
      void onChange();
    } catch {
      toast.error(t(($) => $.toasts.error.workflowNotMoved));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextStep = () => {
    const allUsersResponded = step.users.every(
      (u) => u.responseStatus !== WorkflowStepUserResponses.pending,
    );
    if (!allUsersResponded) {
      setShowConfirmDialog(ConfirmMessage.notAllResponded);
      return;
    }

    const someUserRejected = step.users.some(
      (u) => u.responseStatus === WorkflowStepUserResponses.rejected,
    );
    if (someUserRejected) {
      setShowConfirmDialog(ConfirmMessage.someRejected);
      return;
    }

    void handleTransition("forward");
  };

  const handlePreviousStep = async () => {
    await handleTransition("backward");
  };

  const isActiveStep = step.status === WorkflowStepStatuses.active;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  const { isWaitingForApprovals, approvedCount, totalCount } =
    getApprovalStatus(step);

  const { canStartWorkflow, canMoveBackward, canMoveForward, showControls } =
    getWorkflowStepControlPermissions({
      canManage,
      canPublish: canPublishVersion(selectedVersion),
      isWorkflowStarted,
      isActiveStep,
      isFirstStep,
      isLastStep,
      versionStatus: selectedVersion.status,
    });

  if (!showControls) {
    return children;
  }

  return (
    <>
      {canMoveBackward && (
        <div className="col-start-2 flex justify-center">
          <Button
            variant="outline"
            onClick={handlePreviousStep}
            disabled={isSubmitting}
            className="min-w-48"
          >
            <ArrowBigUp />
            {t(($) => $.commons.workflow.previousStep)}
          </Button>
        </div>
      )}

      {canStartWorkflow && (
        <div className="col-start-2 flex justify-center">
          <Button
            variant="default"
            onClick={() => void handleTransition("forward")}
            disabled={isSubmitting}
            size="sm"
          >
            <Play />
            {t(($) => $.commons.workflow.startWorkflow)}
          </Button>
        </div>
      )}

      {children}

      {canMoveForward && (
        <div className="col-start-2 flex justify-center">
          <Button
            variant="outline"
            onClick={handleNextStep}
            disabled={isSubmitting || isWaitingForApprovals}
            className="min-w-48"
          >
            <ArrowBigDown />
            {isWaitingForApprovals
              ? t(($) => $.commons.workflow.waitingForApprovals, {
                  approvedCount,
                  totalCount,
                })
              : t(($) => $.commons.workflow.nextStep)}
          </Button>
        </div>
      )}

      <AlertDialog
        open={showConfirmDialog !== null}
        onOpenChange={() => setShowConfirmDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{showConfirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {showConfirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(($) => $.commons.workflow.cancel)}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleTransition("forward")}>
              {t(($) => $.commons.workflow.continue)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
